/* eslint-disable max-lines */
/**
 * Shared editor header (#2231) — one chrome for both the transcript and
 * caption editors. Consumers pass a title, subtitle and a flat list of
 * actions. The header decides which actions render as buttons and which
 * spill into the overflow menu based on the row's actual width so the
 * cluster never wraps to a second line.
 *
 * Slots — not flags — for editor-type-specific bits (language switcher,
 * split-view toggle, translation-picker). The header is unaware of
 * whether it lives inside a transcript or a caption; each editor fills
 * the slot with the right JSX.
 */

import {
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { ArrowLeft, MoreHorizontal } from 'lucide-react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { useMediaQuery } from '@utils/hooks/use-media-query.tsx';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';

export type EditorHeaderActionVariant =
  | 'default'
  | 'primary'
  | 'danger'
  | 'success';

/**
 * A single action rendered in the header's right cluster.
 *
 * - `pinned: true`   → always a button, never spills into overflow (Share, Export).
 * - `overflowOnly:true` → always a menu item, never a button (rarely-used).
 * - Otherwise the header measures available width and spills the
 *   lowest-priority tail into the "…" menu.
 */
export interface EditorHeaderAction {
  key: string;
  /** Screen-reader / tooltip / overflow-menu label. Required. */
  label: string;
  icon: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  loading?: boolean;
  variant?: EditorHeaderActionVariant;
  pinned?: boolean;
  overflowOnly?: boolean;
  /**
   * Escape hatch — replace the standard icon-button rendering with a
   * custom node (e.g. a menu/switch). Only used inline; overflow menu
   * still uses `label` + `icon`.
   */
  render?: (ctx: { inOverflow: boolean }) => ReactNode;
  /** Optional keyboard-shortcut hint appended to the tooltip label. */
  shortcut?: string;
  /**
   * Estimated pixel width the rendered item takes on-screen. Defaults
   * to `ICON_BUTTON_WIDTH_PX` for icon buttons; custom `render` items
   * (labeled buttons, menus, switches) should set this to their real
   * measured width so the overflow math doesn't undercount them.
   */
  estimatedWidth?: number;
}

export interface EditorHeaderProps {
  /** Back-to-workspace handler. */
  onBack: () => void;
  /** Aria-label for the back button; falls back to i18n `back-to-workspace`. */
  backLabel?: string;
  /** Document filename (H1). */
  title: string;
  /** Row shown under the title — usually duration · reviewed% · tags. */
  subtitle?: ReactNode;
  /** Optional inline status pill (save status). Sits between title + actions. */
  status?: ReactNode;
  /** Ordered icon-button action list. See `EditorHeaderAction`. */
  actions: EditorHeaderAction[];
  /**
   * Labeled CTA cluster rendered after the icon actions and never
   * subject to the overflow measurement (e.g. Share / Export). Filled
   * by each editor with editor-specific content.
   */
  trailing?: ReactNode;
  /** Optional heading id — hook the editor body's aria-labelledby to it. */
  titleId?: string;
}

const ICON_BUTTON_WIDTH_PX = 44;
const OVERFLOW_BUTTON_WIDTH_PX = 44;

type GetColour = ReturnType<typeof useThemeContext>['getColour'];

function renderIconButton(action: EditorHeaderAction, getColour: GetColour) {
  const commonProps = {
    type: 'button' as const,
    onClick: action.onClick,
    disabled: action.disabled || action.loading,
    'aria-label': action.label,
    'data-action-key': action.key,
    className: cn(
      'inline-flex items-center justify-center h-9 w-9 rounded-2xl border transition-colors shrink-0',
      // Single-outline focus indicator. The ring-based approach stacked
      // on top of the browser's default focus-visible outline (#2289
      // double-ring report); replacing ring with `outline` + negative
      // offset guarantees the indicator lives *inside* the button
      // border and never bleeds into the parent row.
      'hover:opacity-90 outline-none focus-visible:outline-2 focus-visible:outline-primary focus-visible:-outline-offset-2',
      'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:opacity-40'
    )
  };

  const style = {
    backgroundColor:
      action.variant === 'primary'
        ? getColour('activeText')
        : action.variant === 'danger'
          ? 'transparent'
          : getColour('secondLayer'),
    borderColor:
      action.variant === 'danger' ? 'rgba(239,68,68,0.4)' : getColour('border'),
    color:
      action.variant === 'danger'
        ? 'rgb(220,38,38)'
        : action.variant === 'primary'
          ? getColour('firstLayer')
          : getColour('icon')
  };

  const button = (
    <button {...commonProps} style={style}>
      <span
        aria-hidden="true"
        className="inline-flex h-4 w-4 items-center justify-center"
      >
        {action.icon}
      </span>
    </button>
  );

  const tooltipLabel = action.shortcut
    ? `${action.label} · ${action.shortcut}`
    : action.label;

  return <Tooltip label={tooltipLabel}>{button}</Tooltip>;
}

export function EditorHeader({
  onBack,
  backLabel,
  title,
  subtitle,
  status,
  actions,
  trailing,
  titleId
}: EditorHeaderProps) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const actionsRowRef = useRef<HTMLDivElement | null>(null);
  const [availableWidth, setAvailableWidth] = useState<number>(0);

  // #2231 mobile: at narrow widths, every pinned/flexible icon button
  // collapses into the overflow menu — the AC asks for "actions collapse
  // into overflow instead of wrapping" at phone widths. Trailing/status
  // slots use their own `hidden md:*` classes, so this is the only
  // place the pinned-stays-visible rule needs to be relaxed.
  const [isNarrow] = useMediaQuery('(max-width: 767px)');

  // Measure the row's available width so we can compute how many actions
  // fit before spilling into the overflow menu.
  useLayoutEffect(() => {
    const el = actionsRowRef.current;
    if (!el) return;
    const measure = () => setAvailableWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const widthOf = useCallback(
    (a: EditorHeaderAction) => a.estimatedWidth ?? ICON_BUTTON_WIDTH_PX,
    []
  );

  const { visibleActions, overflowActions, minActionsWidth } = useMemo(() => {
    // At phone widths, bypass the normal width-based overflow math and
    // route EVERY action (including pinned) into the overflow menu, so
    // the header stays a single row of back-arrow + title + "…" at
    // 390px regardless of the measured container width.
    if (isNarrow) {
      return {
        visibleActions: [] as EditorHeaderAction[],
        overflowActions: actions
          .filter(a => !a.overflowOnly)
          .concat(actions.filter(a => a.overflowOnly)),
        minActionsWidth: OVERFLOW_BUTTON_WIDTH_PX
      };
    }

    const pinned = actions.filter(a => a.pinned && !a.overflowOnly);
    const overflowForced = actions.filter(a => a.overflowOnly);
    const flexible = actions.filter(a => !a.pinned && !a.overflowOnly);

    // Reserve space for the overflow "…" button whenever we might need it.
    const reservedForOverflow =
      overflowForced.length > 0 ? OVERFLOW_BUTTON_WIDTH_PX : 0;
    const pinnedWidth = pinned.reduce((acc, a) => acc + widthOf(a), 0);
    // Room left after the pinned + the possible overflow button.
    let roomForFlexible = Math.max(
      0,
      availableWidth - pinnedWidth - reservedForOverflow
    );

    // Greedy fit: consume each flexible action's own width. Stops as
    // soon as the next one won't fit — the rest spill to overflow.
    const visibleFlexible: EditorHeaderAction[] = [];
    const spilledFlexible: EditorHeaderAction[] = [];
    let firstOverflowIndex = -1;
    for (let i = 0; i < flexible.length; i++) {
      const a = flexible[i];
      const w = widthOf(a);
      if (w <= roomForFlexible) {
        visibleFlexible.push(a);
        roomForFlexible -= w;
      } else {
        firstOverflowIndex = i;
        break;
      }
    }
    if (firstOverflowIndex >= 0) {
      spilledFlexible.push(...flexible.slice(firstOverflowIndex));
    }

    // If any flexible spilled AND we hadn't already reserved room
    // for the overflow button, we now WILL show it — recompute the
    // visible set with that extra reservation taken out.
    if (spilledFlexible.length > 0 && reservedForOverflow === 0) {
      let recheckRoom = Math.max(
        0,
        availableWidth - pinnedWidth - OVERFLOW_BUTTON_WIDTH_PX
      );
      const rechecked: EditorHeaderAction[] = [];
      for (const a of visibleFlexible) {
        const w = widthOf(a);
        if (w <= recheckRoom) {
          rechecked.push(a);
          recheckRoom -= w;
        } else {
          spilledFlexible.unshift(a);
        }
      }
      visibleFlexible.length = 0;
      visibleFlexible.push(...rechecked);
    }

    // Actions container floor: the pinned buttons must NEVER be
    // squeezed off-screen by a long title. Reserve their total
    // width + one overflow-menu button, so flex distributes any
    // shortage to the left cluster (which has a much higher
    // flex-shrink) instead of clipping pinned items.
    const minActionsWidth = pinnedWidth + OVERFLOW_BUTTON_WIDTH_PX;

    return {
      visibleActions: [...pinned, ...visibleFlexible],
      overflowActions: [...spilledFlexible, ...overflowForced],
      minActionsWidth
    };
  }, [actions, availableWidth, widthOf, isNarrow]);

  const renderOverflowItem = useCallback(
    (action: EditorHeaderAction, close: () => void) => {
      if (action.render) return action.render({ inOverflow: true });
      const onSelect = () => {
        close();
        action.onClick?.();
      };
      return (
        <MenuItem
          as="button"
          disabled={action.disabled || action.loading}
          onClick={onSelect}
          className={cn(
            'flex w-full items-center gap-3 px-3 py-2 text-sm cursor-pointer',
            'data-[focus]:bg-black/5 dark:data-[focus]:bg-white/10',
            'data-[disabled]:opacity-40 data-[disabled]:cursor-not-allowed'
          )}
          style={{
            color:
              action.variant === 'danger'
                ? 'rgb(220,38,38)'
                : getColour('normalText')
          }}
        >
          <span
            aria-hidden="true"
            className="inline-flex h-4 w-4 items-center justify-center"
            style={{ color: getColour('icon') }}
          >
            {action.icon}
          </span>
          <span className="flex-1 text-left">{action.label}</span>
          {action.shortcut && (
            <kbd
              className="ml-auto text-[10px] font-mono px-1.5 py-0.5 rounded border"
              style={{
                borderColor: getColour('hairline'),
                color: getColour('inactiveText')
              }}
            >
              {action.shortcut}
            </kbd>
          )}
        </MenuItem>
      );
    },
    [getColour]
  );

  // Extract a stable id for the H1 so the editor body can aria-labelledby to it.
  const generatedTitleId = useMemo(
    () => titleId ?? 'editor-header-title',
    [titleId]
  );

  // Close overflow menu on Escape — Headless UI handles this, but we
  // also want it to close if the header remounts.
  useEffect(() => {
    return () => {
      /* intentional — remount cleanup no-op; kept for future teardown */
    };
  }, []);

  return (
    <header
      role="banner"
      aria-label={t('editor-header')}
      className="w-full flex-shrink-0"
      style={{ backgroundColor: getColour('baseLayer') }}
    >
      <div
        className={cn(
          // Single row, no wrapping. The title truncates to make room
          // for the actions/trailing cluster; the actions overflow
          // math collapses icons into "…" when there isn't space —
          // meets #2231's "collapse into overflow instead of wrapping"
          // AC at phone widths (390px).
          'flex items-center gap-x-3 px-4 py-2 border-b',
          'shadow-[0_1px_0_0_rgba(0,0,0,0.03)]'
        )}
        style={{
          borderColor: getColour('border'),
          backgroundColor: getColour('secondLayer')
        }}
      >
        {/* Left cluster: back arrow + title + subtitle. Stays inline
            on every width. Grows ~3× faster than the actions row so
            long filenames get the extra width before the icon cluster
            does; a 7rem floor keeps the title readable at phone
            widths (~390px) instead of collapsing to zero width when
            many pinned actions reserve space. */}
        <div className="flex items-center gap-2 min-w-[7rem] [flex:3_1_0%]">
          <Tooltip label={backLabel ?? t('back-to-workspace')}>
            <button
              type="button"
              onClick={onBack}
              aria-label={backLabel ?? t('back-to-workspace')}
              className={cn(
                'inline-flex items-center justify-center h-9 w-9 rounded-2xl shrink-0',
                // Same focus pattern as the action buttons — see renderIconButton (#2289).
                'hover:opacity-80 outline-none focus-visible:outline-2 focus-visible:outline-primary focus-visible:-outline-offset-2'
              )}
              style={{ color: getColour('icon') }}
            >
              <ArrowLeft size={18} aria-hidden="true" />
            </button>
          </Tooltip>

          <div className="flex flex-col min-w-0">
            <h1
              id={generatedTitleId}
              className="text-sm md:text-base font-semibold leading-tight truncate m-0"
              style={{ color: getColour('normalText') }}
              title={title}
            >
              {title}
            </h1>
            {subtitle && (
              <div
                className="text-xs leading-tight truncate mt-0.5"
                style={{ color: getColour('inactiveText') }}
              >
                {subtitle}
              </div>
            )}
          </div>
        </div>

        {/* Status pill */}
        {status && (
          <div className="hidden md:flex items-center shrink-0">{status}</div>
        )}

        {/* Actions cluster — grows to fill remaining width,
            overflow-safe. `overflow-hidden` is a defensive clip so a
            mid-transition size measurement never leaks buttons past
            the viewport edge; the overflow-menu math itself keeps
            the "wrong" buttons off screen on final layout. */}
        <div
          ref={actionsRowRef}
          role="toolbar"
          aria-label={t('editor-actions')}
          className="flex-1 min-w-0 flex items-center justify-end gap-1 overflow-hidden"
          style={{ minWidth: minActionsWidth }}
        >
          {visibleActions.map(action => (
            <div
              key={action.key}
              className="shrink-0"
              data-action-key={action.key}
            >
              {action.render
                ? action.render({ inOverflow: false })
                : renderIconButton(action, getColour)}
            </div>
          ))}

          {overflowActions.length > 0 && (
            <OverflowMenu
              label={t('more-actions')}
              getColour={getColour}
              overflowActions={overflowActions}
              renderOverflowItem={renderOverflowItem}
            />
          )}
        </div>

        {trailing && (
          // Trailing stays mounted at every width — the children own
          // their own narrow-width hiding (`hidden md:*`) so their
          // internal modals keep working even when the visible
          // buttons are hidden. The overflow menu exposes narrow
          // fallback actions that open those same modals via lifted
          // state (#2231).
          <div className="flex items-center gap-1.5 justify-end shrink-0">
            {trailing}
          </div>
        )}
      </div>
    </header>
  );
}

export default EditorHeader;

type OverflowMenuProps = {
  label: string;
  getColour: GetColour;
  overflowActions: EditorHeaderAction[];
  renderOverflowItem: (
    action: EditorHeaderAction,
    close: () => void
  ) => ReactNode;
};

/**
 * Overflow (⋯) menu split out so we can hold a ref to the trigger and
 * blur it when the menu closes. Headless UI correctly returns focus to
 * the trigger on Escape / outside-click (keyboard a11y), but that
 * leaves `:focus-visible` active and paints a focus ring the designer
 * doesn't want sitting on the button after dismissal (#2289 follow-up).
 * Blurring on close drops the indicator; Tab-to-focus still shows it
 * normally for keyboard navigation.
 */
function OverflowMenu({
  label,
  getColour,
  overflowActions,
  renderOverflowItem
}: OverflowMenuProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const wasOpenRef = useRef(false);

  return (
    <Menu as="div" className="relative shrink-0" data-overflow-menu>
      {({ open, close }) => {
        if (wasOpenRef.current && !open) {
          // Fire after Headless UI has restored focus to the trigger so
          // our blur wins the race and the ring doesn't flash.
          queueMicrotask(() => buttonRef.current?.blur());
        }
        wasOpenRef.current = open;
        return (
          <>
            <Tooltip label={label}>
              <MenuButton
                ref={buttonRef}
                aria-label={label}
                className={cn(
                  'inline-flex items-center justify-center h-8 w-8 rounded-2xl border transition-colors',
                  'hover:opacity-90 outline-none focus-visible:outline-2 focus-visible:outline-primary focus-visible:-outline-offset-2'
                )}
                style={{
                  backgroundColor: getColour('secondLayer'),
                  borderColor: getColour('border'),
                  color: getColour('icon')
                }}
              >
                <MoreHorizontal size={16} aria-hidden="true" />
              </MenuButton>
            </Tooltip>
            <MenuItems
              anchor={{ to: 'bottom end', gap: 4 }}
              className="min-w-56 rounded-2xl border shadow-lg py-1 z-50 focus:outline-none"
              style={{
                backgroundColor: getColour('secondLayer'),
                borderColor: getColour('border')
              }}
            >
              {overflowActions.map(action => (
                <div key={action.key}>{renderOverflowItem(action, close)}</div>
              ))}
            </MenuItems>
          </>
        );
      }}
    </Menu>
  );
}
